/**
 * useHotkeys.ts — der EINE Dispatcher (docs/30 §9.3).
 *
 * Genau EIN keydown-Listener, capture-Phase, auf window. Mehrere Listener sind
 * die Ursache jedes „mein Kuerzel feuert doppelt"-Bugs — deshalb hier zentral.
 * Kommandos liefert die Registry (matchCommand); dieser Modul kennt nur die
 * Tasten-Normalisierung und die Textfeld-/IME-Regeln.
 */

import type { NavigateFunction } from 'react-router-dom';
import type { SizeClass } from '../layout/sizeClass';
import type { Replacements } from '../i18n/translate';
import { matchCommand } from './registry';

/** Normalisierte Tastenkombination, z.B. 'mod+shift+t', 'escape', 'mod+alt+KeyI'. */
export type KeySpec = string;

/** Die t-Funktion aus dem I18nProvider (Signatur bewusst hier gespiegelt, um
 *  keinen Laufzeit-Import der i18n-Schicht in den Dispatcher zu ziehen). */
type TFunction = (key: string, replacements?: Replacements) => string;

export interface CommandContext {
  sizeClass: SizeClass;
  pathname: string;
  hasDetail: boolean;
  /** Aktuelle Tiefe des Overlay-Stapels (aus overlayStack.getOverlayDepth). */
  overlayDepth: number;
  navigate: NavigateFunction;
  t: TFunction;
}

// Auf macOS/iPadOS ist die „Meta"-Taste der Kommando-Modifikator, sonst Strg.
const MODIFIER = /Mac|iPad|iPhone/.test(navigator.platform ?? navigator.userAgent) ? 'meta' : 'ctrl';

/**
 * Uebersetzt ein KeyboardEvent in eine stabile KeySpec.
 *
 * KRITISCH ist der altKey-Zweig: ⌥ ersetzt auf macOS/iPadOS das erzeugte
 * Zeichen (⌥I → "ˆ" Dead Key, ⌥1 → "¡"). Mit e.key.toLowerCase() wuerde
 * 'mod+alt+i' deshalb NIE matchen. Fuer Alt-Kombinationen gilt daher die
 * PHYSISCHE Taste (e.code, z.B. 'KeyI'), fuer alles andere das Zeichen.
 */
export function normalize(e: KeyboardEvent): KeySpec {
  const parts: string[] = [];
  if (e.metaKey) parts.push(MODIFIER === 'meta' ? 'mod' : 'meta');
  if (e.ctrlKey) parts.push(MODIFIER === 'ctrl' ? 'mod' : 'ctrl');
  if (e.altKey) parts.push('alt');
  if (e.shiftKey) parts.push('shift');
  parts.push(e.altKey ? e.code : e.key.toLowerCase()); // 'KeyI' bzw. 'k'
  return parts.join('+');
}

function isTextEntry(el: EventTarget | null): boolean {
  const n = el as HTMLElement | null;
  if (!n) return false;
  return n.tagName === 'INPUT' || n.tagName === 'TEXTAREA' || n.isContentEditable;
}

/**
 * Installiert den EINEN keydown-Listener. Liefert eine Abmeldefunktion, die den
 * exakt selben Listener wieder entfernt (gleiche Referenz + Optionen).
 */
export function installHotkeys(getCtx: () => CommandContext): () => void {
  const onKeyDown = (e: KeyboardEvent) => {
    // IME/Diktat: keydown waehrend einer Komposition NIE als Kuerzel deuten.
    if (e.isComposing || e.keyCode === 229) return;

    const spec = normalize(e);

    // In Textfeldern nur Kombinationen mit Modifikator und Escape zulassen —
    // sonst kapert '/' oder '?' jede Eingabe. AltGr-Falle (E4a-Verify): AltGr
    // meldet auf Windows/Linux ctrlKey+altKey und ERZEUGT dort Zeichen
    // (ungarisch AltGr+I = í) — ctrl+alt zaehlt in Textfeldern deshalb als
    // Texteingabe, nicht als Kommando-Kandidat.
    if (isTextEntry(e.target) && spec !== 'escape') {
      const commandModifier = e.metaKey || (e.ctrlKey && !e.altKey);
      if (!commandModifier) return;
    }

    const ctx = getCtx();
    const cmd = matchCommand(spec, ctx); // when() + scope + overlayDepth
    if (!cmd) return;

    // Nicht-Wiederhol-Kommandos: Festhalten schaltet nicht durch.
    if (cmd.noRepeat && e.repeat) return;

    e.preventDefault();
    e.stopPropagation();
    void cmd.run(ctx);
  };

  window.addEventListener('keydown', onKeyDown, { capture: true });
  return () => window.removeEventListener('keydown', onKeyDown, { capture: true });
}
